import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { serviceApiV3, warehouseApi } from '@/api/client';
import type { ResponseArray, ResponseObject } from '@/types/shared';

/**
 * Cloud capacity (ServiceMgmt) — the "Cloud Resources" side of the Capacities
 * dashboard. Talks to ServiceMgmt via `serviceApiV3` (plain access token). The
 * envelope here is `{ status, data }` (NOT the warehouse `{ result }` shape), so
 * responses are unwrapped from `.data`.
 */

/* ── Response shapes (live-probed) ─────────────────────────────────────────── */

export interface CloudCapacitySummary {
  total_zones: number;
  total_hosts: number;
  total_vms: number;
  healthy_hosts: number;
  cpu_usage_pct: number;
  memory_usage_pct: number;
  storage_usage_pct: number;
}

export interface CloudHost {
  id: string;
  name: string;
  status: string;
  cpu_cores: number;
  memory_mb: number;
  vm_count: number;
  cpu_usage_pct: number;
  memory_usage_pct: number;
  zone_id: string;
  zone_name: string;
}

export interface CloudCompute {
  total_cpu_cores: number;
  used_cpu_cores: number;
  total_memory_gb: number;
  used_memory_gb: number;
  avg_cpu_usage_pct: number;
  avg_memory_usage_pct: number;
  hosts: CloudHost[];
}

export interface CloudDatastore {
  id: string;
  name: string;
  type: string;
  capacity_gb: number;
  free_gb: number;
  used_gb: number;
  accessible: boolean;
  zone_id: string;
  zone_name: string;
}

export interface CloudStorage {
  total_capacity_gb: number;
  used_gb: number;
  free_gb: number;
  usage_pct: number;
  datastores: CloudDatastore[];
}

export interface CloudNetwork {
  id: string;
  name: string;
  type?: string;
  zone_id?: string;
  zone_name?: string;
}

export interface CloudNetworkSummary {
  total_networks: number;
  public_networks: number;
  private_networks: number;
  networks: CloudNetwork[];
}

export interface CloudZone {
  zone_id: string;
  zone_name: string;
  provider_name: string;
  provider_type: string;
  status: string;
  error?: string;
  /** Per-zone aggregates derived client-side from hosts + datastores. */
  host_count?: number;
  vm_count?: number;
  cpu_usage_pct?: number;
  memory_usage_pct?: number;
  storage_gb?: number;
}

export interface CloudCapacity {
  summary: CloudCapacitySummary;
  compute: CloudCompute;
  storage: CloudStorage;
  network: CloudNetworkSummary;
  by_zone: CloudZone[];
}

/** One VM in a zone's embedded `instances[]` (spec is nested). */
export interface CloudZoneInstance {
  uid: string;
  name: string;
  status: string;
  host_id?: string;
  host_name?: string;
  spec?: { cpu?: number; memory_mb?: number; disk_gb?: number };
  zone_id?: string;
  zone_name?: string;
}

/**
 * `GET /capacities/zone/:id` — a SINGLE zone's full capacity. Unlike the list
 * (`/capacities`) this has no `summary`/`by_zone`; the zone's own identity is at
 * top level and hosts/datastores/networks/instances are embedded (so no separate
 * — and staging-flaky — `/zones/:id/*` calls are needed).
 */
export interface CloudZoneDetail {
  zone_id: string;
  zone_name: string;
  provider_name?: string;
  provider_type?: string;
  provider_code?: string;
  status: string;
  compute: CloudCompute;
  storage: CloudStorage;
  network: CloudNetworkSummary;
  instances: CloudZoneInstance[];
}

/** ServiceMgmt `{ status, data }` envelope. */
interface CloudEnvelope<T> {
  status: string;
  data: T;
}

/* ── Query ─────────────────────────────────────────────────────────────────── */

export const cloudCapacityKeys = {
  compute: ['capacities', 'cloud', 'compute'] as const,
};

async function getCloudCapacity(): Promise<CloudCapacity> {
  const { data } = await serviceApiV3.get<CloudEnvelope<CloudCapacity>>('/capacities', {
    params: { provider_type: 'compute' },
  });
  return data.data;
}

/** Live cloud capacity for the "Cloud Resources" tab. Read-only. */
export const useCloudCapacity = () =>
  useQuery({
    queryKey: cloudCapacityKeys.compute,
    queryFn: getCloudCapacity,
    staleTime: 5 * 60 * 1000,
  });

/* ── Cloud drill-down (zones / hosts / datastores / instances / mapping / sync) ──
 * All ServiceMgmt (`serviceApiV3`). Some endpoints are backend-flaky on staging
 * (`/instances` 401 customer-resolve, `/capacity/*` 404) — hooks are wired; the
 * UI degrades to empty/error states when the service errors. */

const unwrap = async <T>(p: string, params?: Record<string, unknown>): Promise<T> => {
  const { data } = await serviceApiV3.get<CloudEnvelope<T>>(p, params ? { params } : undefined);
  return data.data;
};

export interface CloudInstance {
  id: string;
  name?: string;
  status?: string;
  vcpus?: number;
  memory_mb?: number;
  host_id?: string;
  host_name?: string;
  zone_id?: string;
  zone_name?: string;
  [key: string]: unknown;
}

export interface EquipmentMapping {
  id: string;
  item?: unknown;
  zone_id?: string;
  host_id?: string;
  provider_type?: string;
  provider_code?: string;
  is_active?: boolean;
  [key: string]: unknown;
}

/**
 * Full capacity for one zone — hosts, datastores, networks and instances are all
 * embedded in this single response, so the drill-down needs no other calls.
 */
export const useZoneCapacity = (zoneId: string) =>
  useQuery({ queryKey: ['capacities', 'cloud', 'zone', zoneId], queryFn: () => unwrap<CloudZoneDetail>(`/capacities/zone/${zoneId}`), enabled: !!zoneId, retry: false });

export const useInstances = (params?: Record<string, unknown>) =>
  useQuery({ queryKey: ['capacities', 'cloud', 'instances', params], queryFn: () => unwrap<CloudInstance[]>('/instances', params), retry: false });
export const useInstance = (id: string) =>
  useQuery({ queryKey: ['capacities', 'cloud', 'instance', id], queryFn: () => unwrap<CloudInstance>(`/instances/${id}`), enabled: !!id, retry: false });
export const useInstanceMonitoring = (id: string) =>
  useQuery({ queryKey: ['capacities', 'cloud', 'instance-monitoring', id], queryFn: () => unwrap<Record<string, unknown>>(`/instances/${id}/monitoring`), enabled: !!id, retry: false });

/**
 * Equipment↔host mappings + sync jobs live on the WAREHOUSE backend
 * (`warehouseApi`, `{results}`/`{result}` envelope) — NOT ServiceMgmt. Legacy:
 * `warehouse.get('/capacity/mappings')`, `warehouse.get('/capacity/jobs')`,
 * `warehouse.post('/capacity/sync')`.
 */
export const useEquipmentMappings = () =>
  useQuery({
    queryKey: ['capacities', 'cloud', 'mappings'],
    queryFn: async () => (await warehouseApi.get<ResponseArray<EquipmentMapping>>('/capacity/mappings')).data.results,
    retry: false,
  });
export const useSyncJobs = () =>
  useQuery({
    queryKey: ['capacities', 'cloud', 'sync-jobs'],
    queryFn: async () => (await warehouseApi.get<ResponseArray<Record<string, unknown>>>('/capacity/jobs')).data.results,
    retry: false,
  });

/** Trigger a capacity sync (live action — user-initiated, not the assistant). */
export const useTriggerSync = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (body?: unknown) => {
      const { data } = await warehouseApi.post<ResponseObject<unknown>>('/capacity/sync', body ?? {});
      return data.result;
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ['capacities', 'cloud'] }),
  });
};
